import React, { useState, useEffect, Fragment } from "react";
import API from "../../services/API";
import { toast } from "react-toastify";

const SchoolHolidayConfig = () => {
  // -------------------------------
  // HOLIDAY STATES
  // -------------------------------
  const [holidays, setHolidays] = useState([]);
  const [holidayForm, setHolidayForm] = useState({
    holiday_name: "",
    start_date: "",
    end_date: "",
    is_recurring: false,
    description: "",
  });
  const [holidayLoading, setHolidayLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedHoliday, setSelectedHoliday] = useState(null);

  // -------------------------------
  // EXTRA DAY STATES
  // -------------------------------
  const [extraDays, setExtraDays] = useState([]);
  const [extraForm, setExtraForm] = useState({
    extra_name: "",
    start_date: "",
    end_date: "",
    is_recurring: false,
    description: "",
  });
  const [extraLoading, setExtraLoading] = useState(false);
  const [showExtraDeleteModal, setShowExtraDeleteModal] = useState(false);
  const [selectedExtra, setSelectedExtra] = useState(null);

  // ==============================
  // 📅 FETCH HOLIDAYS
  // ==============================
  const fetchHolidays = async () => {
    setHolidayLoading(true);
    try {
      const headers = {
        "Content-Type": "application/json",
        token: localStorage.getItem("token"),
      };
      const response = await API.get("/school/holidays?is_portal=true", { headers });
      if (response.data.response_code === 1000) {
        setHolidays(response.data.data || []);
      } else toast.error(response.data.message || "Failed to fetch holidays");
    } catch (err) {
      console.error("Fetch error:", err);
      toast.error("Error fetching holidays");
    } finally {
      setHolidayLoading(false);
    }
  };

  // ==============================
  // 📘 FETCH EXTRA DAYS
  // ==============================
  const fetchExtraDays = async () => {
    setExtraLoading(true);
    try {
      const headers = {
        "Content-Type": "application/json",
        token: localStorage.getItem("token"),
      };
      const response = await API.get("/school/extraDays?is_portal=true", { headers });
      if (response.data.response_code === 1000) {
        setExtraDays(response.data.data || []);
      } else toast.error(response.data.message || "Failed to fetch extra days");
    } catch (err) {
      console.error("Fetch error:", err);
      toast.error("Error fetching extra days");
    } finally {
      setExtraLoading(false);
    }
  };

  useEffect(() => {
    fetchHolidays();
    fetchExtraDays();
  }, []);

  // Handle changes
  const handleHolidayChange = (e) => {
    const { name, value, type, checked } = e.target;
    setHolidayForm({
      ...holidayForm,
      [name]: type === "checkbox" ? checked : value,
    });
  };
  const handleExtraChange = (e) => {
    const { name, value, type, checked } = e.target;
    setExtraForm({
      ...extraForm,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  // ==============================
  // ADD HOLIDAY
  // ==============================
  const handleAddHoliday = async () => {
    if (!holidayForm.holiday_name || !holidayForm.start_date || !holidayForm.end_date)
      return toast.error("Please fill in all required fields");

    try {
      const headers = {
        "Content-Type": "application/json",
        token: localStorage.getItem("token"),
      };
      const response = await API.post("/school/holidays?is_portal=true", holidayForm, { headers });
      if (response.data.response_code === 1000) {
        toast.success("Holiday saved successfully");
        setHolidayForm({
          holiday_name: "",
          start_date: "",
          end_date: "",
          is_recurring: false,
          description: "",
        });
        fetchHolidays();
      } else toast.error(response.data.message || "Failed to add holiday");
    } catch (err) {
      console.error("Add error:", err);
      toast.error("Error adding holiday");
    }
  };

  // ==============================
  // ADD EXTRA DAY
  // ==============================
  const handleAddExtraDay = async () => {
    if (!extraForm.extra_name || !extraForm.start_date || !extraForm.end_date)
      return toast.error("Please fill in all required fields");

    try {
      const headers = {
        "Content-Type": "application/json",
        token: localStorage.getItem("token"),
      };
      const response = await API.post("/school/extraDays?is_portal=true", extraForm, { headers });
      if (response.data.response_code === 1000) {
        toast.success("Extra school day saved successfully");
        setExtraForm({
          extra_name: "",
          start_date: "",
          end_date: "",
          is_recurring: false,
          description: "",
        });
        fetchExtraDays();
      } else toast.error(response.data.message || "Failed to add extra day");
    } catch (err) {
      console.error("Add error:", err);
      toast.error("Error adding extra day");
    }
  };

  // ==============================
  // DELETE HANDLERS
  // ==============================
  const openDeleteModal = (h) => {
    setSelectedHoliday(h);
    setShowDeleteModal(true);
  };
  const confirmDeleteHoliday = async () => {
    if (!selectedHoliday) return;
    try {
      const headers = {
        "Content-Type": "application/json",
        token: localStorage.getItem("token"),
      };
      const response = await API.delete(`/school/holidays/${selectedHoliday.id}?is_portal=true`, { headers });
      if (response.data.response_code === 1000) {
        toast.success("Holiday deleted");
        fetchHolidays();
      } else toast.error(response.data.message || "Failed to delete");
    } catch (err) {
      console.error("Delete error:", err);
      toast.error("Error deleting holiday");
    } finally {
      setShowDeleteModal(false);
      setSelectedHoliday(null);
    }
  };

  const openExtraDeleteModal = (e) => {
    setSelectedExtra(e);
    setShowExtraDeleteModal(true);
  };
  const confirmDeleteExtra = async () => {
    if (!selectedExtra) return;
    try {
      const headers = {
        "Content-Type": "application/json",
        token: localStorage.getItem("token"),
      };
      const response = await API.delete(`/school/extraDays/${selectedExtra.id}?is_portal=true`, { headers });
      if (response.data.response_code === 1000) {
        toast.success("Extra day deleted");
        fetchExtraDays();
      } else toast.error(response.data.message || "Failed to delete");
    } catch (err) {
      console.error("Delete error:", err);
      toast.error("Error deleting extra day");
    } finally {
      setShowExtraDeleteModal(false);
      setSelectedExtra(null);
    }
  };

  // ==============================
  // RENDER
  // ==============================
  return (
    <Fragment>
      <div className="container-fluid">

        <div className="card mb-4">
          <div className="card-header bg-dark">
            <h4>School Holidays</h4>
          </div>

          {/* Add Form */}
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-3">
                <label>Holiday Name</label>
                <input type="text" className="form-control" name="holiday_name"
                  value={holidayForm.holiday_name} onChange={handleHolidayChange} placeholder="e.g., Deepavali" />
              </div>
              <div className="col-md-3">
                <label>Start Date</label>
                <input type="date" className="form-control" name="start_date"
                  value={holidayForm.start_date} onChange={handleHolidayChange} />
              </div>
              <div className="col-md-3">
                <label>End Date</label>
                <input type="date" className="form-control" name="end_date"
                  value={holidayForm.end_date} onChange={handleHolidayChange} />
              </div>
              <div className="col-md-12">
                <label>Description</label>
                <textarea name="description" className="form-control" rows={2}
                  placeholder="Optional notes..." value={holidayForm.description}
                  onChange={handleHolidayChange}></textarea>
              </div>
              <div className="col-md-12 text-end">
                <button className="btn btn-primary" onClick={handleAddHoliday}>Add Holiday</button>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="card-body">
            <h5>Existing Holidays</h5>
            {holidayLoading ? <p>Loading...</p> :
              holidays.length === 0 ? <p>No holidays found.</p> :
                <table className="table table-striped">
                  <thead><tr>
                    <th>#</th><th>Holiday</th><th>Start</th><th>End</th><th>Recurring</th><th>Description</th><th>Action</th>
                  </tr></thead>
                  <tbody>
                    {holidays.map((h, i) => (
                      <tr key={h.id}>
                        <td>{i + 1}</td>
                        <td>{h.holiday_name}</td>
                        <td>{h.start_date}</td>
                        <td>{h.end_date}</td>
                        <td>{h.is_recurring ? "Yes" : "No"}</td>
                        <td>{h.description || "-"}</td>
                        <td><button className="btn btn-danger btn-sm" onClick={() => openDeleteModal(h)}>Delete</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>}
          </div>
        </div>

        {/* 🧾 EXTRA SCHOOL DAYS SECTION */}
        <div className="card">
          <div className="card-header bg-dark">
            <h4>Extra School Days</h4>
          </div>

          {/* Add Form */}
          <div className="card-body">
            <div className="row g-3">
              <div className="col-md-3">
                <label>Extra Day Name</label>
                <input type="text" className="form-control" name="extra_name"
                  value={extraForm.extra_name} onChange={handleExtraChange} placeholder="e.g., Replacement for Deepavali" />
              </div>
              <div className="col-md-3">
                <label>Start Date</label>
                <input type="date" className="form-control" name="start_date"
                  value={extraForm.start_date} onChange={handleExtraChange} />
              </div>
              <div className="col-md-3">
                <label>End Date</label>
                <input type="date" className="form-control" name="end_date"
                  value={extraForm.end_date} onChange={handleExtraChange} />
              </div>
              <div className="col-md-12">
                <label>Description</label>
                <textarea name="description" className="form-control" rows={2}
                  placeholder="Optional notes..." value={extraForm.description}
                  onChange={handleExtraChange}></textarea>
              </div>
              <div className="col-md-12 text-end">
                <button className="btn btn-primary" onClick={handleAddExtraDay}>Add Extra Day</button>
              </div>
            </div>
          </div>

          {/* Table */}
          <div className="card-body">
            <h5>Existing Extra School Days</h5>
            {extraLoading ? <p>Loading...</p> :
              extraDays.length === 0 ? <p>No extra days found.</p> :
                <table className="table table-striped">
                  <thead><tr>
                    <th>#</th><th>Extra Day</th><th>Start</th><th>End</th><th>Recurring</th><th>Description</th><th>Action</th>
                  </tr></thead>
                  <tbody>
                    {extraDays.map((e, i) => (
                      <tr key={e.id}>
                        <td>{i + 1}</td>
                        <td>{e.extra_name}</td>
                        <td>{e.start_date}</td>
                        <td>{e.end_date}</td>
                        <td>{e.is_recurring ? "Yes" : "No"}</td>
                        <td>{e.description || "-"}</td>
                        <td><button className="btn btn-danger btn-sm" onClick={() => openExtraDeleteModal(e)}>Delete</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>}
          </div>
        </div>
      </div>

      {/* 🗑 DELETE MODALS */}
      {showDeleteModal && (
        <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered"><div className="modal-content">
            <div className="modal-header"><h5>Confirm Delete</h5>
              <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)}></button></div>
            <div className="modal-body">
              Are you sure you want to delete <strong>{selectedHoliday?.holiday_name}</strong>?
              <p className="text-danger small">This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>Cancel</button>
              <button className="btn btn-danger" onClick={confirmDeleteHoliday}>Delete</button>
            </div>
          </div></div>
        </div>
      )}

      {showExtraDeleteModal && (
        <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog modal-dialog-centered"><div className="modal-content">
            <div className="modal-header bg-warning">
              <h5>Confirm Delete</h5>
              <button type="button" className="btn-close" onClick={() => setShowExtraDeleteModal(false)}></button>
            </div>
            <div className="modal-body">
              Are you sure you want to delete <strong>{selectedExtra?.extra_name}</strong>?
              <p className="text-danger small">This action cannot be undone.</p>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowExtraDeleteModal(false)}>Cancel</button>
              <button className="btn btn-danger" onClick={confirmDeleteExtra}>Delete</button>
            </div>
          </div></div>
        </div>
      )}
    </Fragment>
  );
};

export default SchoolHolidayConfig;
